<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('price.pricesLists') }}</span>
        <h3 class="page-title">
          {{ $t('price.priceEdit') }}
          <template v-if="$route.meta.type === 'products'">{{ $t('price.productGenitive') }}</template>
          <template v-if="$route.meta.type === 'services'">{{ $t('price.serviceGenitive') }}</template>
          <template v-if="$route.meta.type === 'setups'">{{ $t('price.setup') }}</template>
        </h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push(backUrl)"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          {{ $t('ui.saveChanges') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="price-edit-form" tag="form" v-if="!loadError">
              <d-row>
                <d-col
                  cols="12"
                  :md="$route.meta.type === 'services' || $route.meta.type === 'setups' ? 8 : 12"
                  :lg="$route.meta.type === 'services' || $route.meta.type === 'setups' ? 8 : 12">
                  <div class="form-group">
                    <label for="name">
                      <template v-if="$route.meta.type === 'products'">{{ $t('product.product') }}</template>
                      <template v-if="$route.meta.type === 'services' || $route.meta.type === 'setups'">{{ $t('service.service') }}</template>
                    </label>

                    <validation-provider
                      v-if="allDataLoaded && $route.meta.type === 'products'"
                      name="product-id"
                      v-slot="{ errors }">
                      <select-product
                        :productID="product_id"
                        :class="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>

                    <validation-provider
                      v-if="allDataLoaded && $route.meta.type !== 'products'"
                      name="service-id"
                      v-slot="{ errors }">
                      <select-service
                        :serviceID="service_id"
                        :class="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  v-if="$route.meta.type === 'services' || $route.meta.type === 'setups'"
                  cols="12" md="4" lg="4">
                  <div class="form-group">
                    <label for="name">
                      <template v-if="$route.meta.type === 'services'">{{ $t('price.quantityFrom') }}:</template>
                      <template v-if="$route.meta.type === 'setups'">{{ $t('price.quantityTo') }}:</template>
                    </label>

                    <validation-provider
                      rules="required|min_value:1"
                      name="value-from"
                      v-slot="{ errors }">
                      <d-input
                        type="number"
                        :state="errors.length ? 'invalid' : null"
                        v-model="value_from" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  cols="12"
                  :md="$route.meta.type === 'products' ? 4 : 8"
                  :lg="$route.meta.type === 'products' ? 4 : 8">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('price.price') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0"
                      name="price"
                      v-slot="{ errors }">
                      <input
                        :class="{
                          'form-control': true,
                          'is-invalid': errors.length
                        }"
                        :invalid="errors.length ? true : null"
                        type="number"
                        v-model="price" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  v-if="$route.meta.type === 'products'"
                  cols="12"
                  md="4"
                  lg="4">
                  <div class="form-group pt-4">
                    <d-checkbox
                      inline
                      v-model="is_sale"
                      toggle
                      class="mt-2">
                      {{ $t('price.isSale') }}
                    </d-checkbox>
                  </div>
                </d-col>

                <d-col cols="12" md="4" lg="4">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('settings.currency.currency') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="currency-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="currency_id"
                        :state="errors.length ? 'invalid' : null"
                        :options="currenciesOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
            </validation-observer>

            <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';
import SelectProduct from './../common/SelectProduct.vue';
import SelectService from './../common/SelectService.vue';

export default {
  name: 'price-edit',
  components: {
    'select-product': SelectProduct,
    'select-service': SelectService
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedCurrencies;
    },
    backUrl () {
      if (this.$route.meta.type === 'products') {
        return '/cenniki/produkty/';
      }

      if (this.$route.meta.type === 'services') {
        return '/cenniki/zdobienia/';
      }

      if (this.$route.meta.type === 'setups') {
        return '/cenniki/setup/';
      }

      return '';
    },
    currenciesOptions () {
      return CommonGetters.getCurrenciesForSelect(this.currencies);
    },
    endpointBase () {
      if (this.$route.meta.type === 'products') {
        return '/products/prices/';
      }

      if (this.$route.meta.type === 'services' || this.$route.meta.type === 'setups') {
        return '/services/prices/';
      }

      return '';
    }
  },
  data() {
    return {
      id: 0,
      product_id: 0,
      service_id: 0,
      currency_id: 0,
      price: '',
      value_from: '',
      is_sale: false,
      currencies: [],
      loadedData: false,
      loadedCurrencies: false,
      loadError: false
    };
  },
  mounted () {
    let fieldsToSet = ['product_id', 'currency_id', 'price', 'is_sale'];

    if (this.$route.meta.type === 'services' || this.$route.meta.type === 'setups') {
      fieldsToSet = ['service_id', 'currency_id', 'price', 'value_from'];
    }

    FormUtils.loadItemData(this, {
      method: 'get',
      endpointBase: this.endpointBase + 'item/',
      instanceFields: fieldsToSet,
      loadedKey: 'loadedData',
      successAction: () => {
        if (this.$route.meta.type === 'products') {
          this.is_sale = !!this.is_sale;
        }
      }
    });

    this.loadAdditionalData();
    this.$bus.$on('ui-select-service-updated', this.updateServiceID);
    this.$bus.$on('ui-select-product-updated', this.updateProductID);
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['price-edit-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend;

      if (this.$route.meta.type === 'products') {
        formDataToSend = {
          product_id: this.product_id,
          currency_id: this.currency_id,
          price: this.price,
          is_sale: this.is_sale
        };
      }

      if (this.$route.meta.type === 'services') {
        formDataToSend = {
          service_id: this.service_id,
          currency_id: this.currency_id,
          value_from: this.value_from,
          price: this.price,
          is_setup: 0
        };
      }

      if (this.$route.meta.type === 'setups') {
        formDataToSend = {
          service_id: this.service_id,
          currency_id: this.currency_id,
          value_from: this.value_from,
          price: this.price,
          is_setup: 1
        };
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: this.endpointBase + 'update/' + this.id,
        formData: formDataToSend,
        successTitle: this.$t('price.savePriceSuccessTitle'),
        successText: this.$t('price.savePriceSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('price.savePriceErrorText'),
        successAction: () => {
          this.$router.push(this.backUrl)
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    updateServiceID (newServiceID) {
      this.service_id = newServiceID;
    },
    updateProductID (newProductID) {
      this.product_id = newProductID;
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-service-updated', this.updateServiceID);
    this.$bus.$off('ui-select-product-updated', this.updateProductID);
  }
};
</script>
